import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getCases, getStats } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/Layout';
import { 
  Briefcase, 
  FileText, 
  FileCheck, 
  Users, 
  Plus, 
  ArrowRight,
  Clock,
  Shield
} from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [casesRes, statsRes] = await Promise.all([
        getCases(),
        getStats()
      ]);
      setCases(casesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'My Cases', value: stats?.own_cases || 0, icon: Briefcase, color: 'text-navy dark:text-blue-400' },
    { label: 'Shared Cases', value: stats?.shared_cases || 0, icon: Users, color: 'text-bronze' },
    { label: 'Documents', value: stats?.total_documents || 0, icon: FileText, color: 'text-muted-foreground' },
    { label: 'Exhibits', value: stats?.total_exhibits || 0, icon: FileCheck, color: 'text-success' },
  ];

  const recentCases = cases.slice(0, 5);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-xl sm:text-3xl font-bold tracking-tight text-foreground">
              Welcome back, {user?.name?.split(' ')[0] || 'Counselor'}
            </h1>
            <p className="font-body text-sm sm:text-base text-muted-foreground mt-1">
              Here's an overview of your cases and documents
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/security">
              <Button variant="outline" size="sm" className="font-body" data-testid="security-settings-btn">
                <Shield className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Security</span>
              </Button>
            </Link>
            <Link to="/cases/new">
              <Button size="sm" className="bg-navy hover:bg-navy-light font-body" data-testid="new-case-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Case</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statCards.map((stat, i) => (
            <Card 
              key={stat.label} 
              className="border-border shadow-sm animate-fade-in"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">
                      {stat.label}
                    </p>
                    <p className="font-heading text-xl sm:text-3xl font-bold mt-1 text-foreground">
                      {loading ? '—' : stat.value}
                    </p>
                  </div>
                  <div className={`p-2 sm:p-3 rounded-lg bg-muted ${stat.color}`}>
                    <stat.icon className="w-4 h-4 sm:w-6 sm:h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Cases */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="lg:col-span-2 border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6">
              <div>
                <CardTitle className="font-heading text-lg sm:text-xl">Recent Cases</CardTitle>
                <CardDescription className="font-body text-xs sm:text-sm">Your most recently updated cases</CardDescription>
              </div>
              <Link to="/cases">
                <Button variant="ghost" size="sm" className="font-body text-bronze" data-testid="view-all-cases-btn">
                  <span className="hidden sm:inline">View all</span>
                  <ArrowRight className="w-4 h-4 sm:ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : recentCases.length > 0 ? (
                <div className="space-y-3">
                  {recentCases.map((caseItem) => (
                    <Link 
                      key={caseItem.id} 
                      to={`/cases/${caseItem.id}`}
                      className="block"
                      data-testid={`case-item-${caseItem.id}`}
                    >
                      <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-bronze/50 hover:bg-muted/50 transition-all duration-200">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-md bg-navy/10 dark:bg-navy-light/20 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-navy dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-body font-semibold text-foreground">{caseItem.title}</p>
                            <p className="font-body text-sm text-muted-foreground">
                              {caseItem.client_name} • {caseItem.document_count} documents
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {caseItem.case_number && (
                            <Badge variant="secondary" className="font-mono text-xs">
                              {caseItem.case_number}
                            </Badge>
                          )}
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="font-body text-muted-foreground">No cases yet</p>
                  <Link to="/cases/new">
                    <Button className="mt-4 bg-navy hover:bg-navy-light font-body" data-testid="create-first-case-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create your first case
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Quick Actions</CardTitle>
              <CardDescription className="font-body">Common tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/cases/new" className="block">
                <Button variant="outline" className="w-full justify-start font-body" data-testid="quick-new-case-btn">
                  <Plus className="w-4 h-4 mr-3" />
                  Create New Case
                </Button>
              </Link>
              <Link to="/cases" className="block">
                <Button variant="outline" className="w-full justify-start font-body" data-testid="quick-view-cases-btn">
                  <Briefcase className="w-4 h-4 mr-3" />
                  View All Cases
                </Button>
              </Link>
              <Link to="/search" className="block">
                <Button variant="outline" className="w-full justify-start font-body" data-testid="quick-search-btn">
                  <FileText className="w-4 h-4 mr-3" />
                  Search Documents
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Getting Started */}
        {cases.length === 0 && !loading && (
          <Card className="border-bronze/30 bg-bronze/5">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-8 h-8 text-bronze" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    Get started in minutes
                  </h3>
                  <p className="font-body text-muted-foreground max-w-lg">
                    Create a case, upload your documents, describe your arguments, and let AI identify 
                    the most relevant exhibits for your trial. It's that simple.
                  </p>
                </div>
                <Link to="/cases/new" className="flex-shrink-0">
                  <Button className="bg-bronze hover:bg-bronze-light font-body" data-testid="get-started-btn">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
